// The Employee Score drill-down on the Red Flags page: one employee's last six monthly
// scores, ending at the month the column shows, and their 6-month average. Everything it
// draws arrives with the page's single six-month read, so opening it fetches nothing.
import { X } from 'lucide-react';
import {
  formatScore,
  scoreMonthLabel,
  type EmployeeScoreSummary,
} from '../../utils/redFlagEmployeeScore';

export default function EmployeeScoreHistoryModal({
  emp,
  summary,
  onClose,
}: {
  emp: { code: string; name: string };
  summary: EmployeeScoreSummary;
  onClose: () => void;
}) {
  // Bars are drawn against the best of the six, so the shape shows the trend whatever
  // scale the project's scorecard uses.
  const top = Math.max(0, ...summary.history.map((h) => h.score ?? 0));
  const newestFirst = [...summary.history].reverse();
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-label={`Employee Score — ${emp.name}`}
        className="w-full max-w-md overflow-hidden rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
          <div>
            <div className="text-base font-semibold text-gray-900">Employee Score — {emp.name}</div>
            <div className="font-mono text-[11px] text-gray-400">{emp.code}</div>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <table className="w-full text-sm">
          <tbody className="divide-y divide-gray-100">
            {newestFirst.map((h) => (
              <tr key={h.month}>
                <td className="w-24 whitespace-nowrap px-5 py-2 text-gray-600">{scoreMonthLabel(h.month)}</td>
                <td className="px-2 py-2">
                  {h.score != null && top > 0 && (
                    <div className="h-2 rounded bg-gray-100">
                      <div
                        className="h-2 rounded bg-primary-500"
                        style={{ width: `${Math.max(2, Math.round((h.score / top) * 100))}%` }}
                      />
                    </div>
                  )}
                </td>
                <td className="w-28 whitespace-nowrap px-5 py-2 text-right tabular-nums">
                  {h.score != null ? (
                    <span className="font-semibold text-gray-900">{formatScore(h.score)}</span>
                  ) : (
                    <span className="text-gray-400" title={h.notJoined ? 'Had not joined yet' : 'No score stored for this month'}>
                      {h.notJoined ? 'Not joined' : '—'}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-5 py-2.5 text-sm">
          <span className="text-gray-600">6-month average</span>
          <span className="font-semibold tabular-nums text-gray-900">
            {summary.avg != null ? formatScore(summary.avg) : '—'}
          </span>
        </div>
      </div>
    </div>
  );
}
